import { computed } from 'vue';
import {
  useGetV7MenuPath,
  usePatchV7MenuPathAnalysis,
  usePatchV7MenuPathDashboard,
  usePostV7MenuPathAnalysis,
  usePostV7MenuPathDashboard,
  getGetV7MenuPathQueryKey,
  type GetV7MenuPathQueryResult,
} from '@/api-client/dashboard/queries/integrated-dashboard-gnb-v7-controller/integrated-dashboard-gnb-v7-controller';
import {
  IntegrateDashboardAnalysisPatchRequest,
  IntegrateDashboardGnbPatchRequest,
} from '@/api-client/dashboard/model';
import { defineStore } from 'pinia';
import { VIEW_MODE } from '@/common/define/common.define';
import type { IconType } from '@/types/icon.types';
import { convertMenuPaths, convertPlatform } from '@/dashboardList/utils/menu.utils';
import { queryClient } from '@/common/lib/queryClient';
import { useViewModeStore } from './view-mode';

export interface PlatformDashboardValue {
  name: string;
  favoriteId?: number;
  iconId?: IconType;
}

export const PLATFORM_MENU = {
  INFRA: 'infrastructure',
  MESSAGE_QUEUE: 'message-queue',
  CLOUD: 'cloud',
  KUBE: 'k8s',
  APP: 'application',
  DB: 'database',
  BUSINESS: 'business',
  LOGS: 'logs',
  ALERT: 'alert',
} as const;

const MAXGAUGE_PLATFORM_MENU = {
  ORACLE: 'oracle',
  MYSQL: 'mysql',
  POSTGRESQL: 'postgresql',
  SQLSERVER: 'sqlserver',
  REDIS: 'redis',
  CUBRID: 'cubrid',
  MONGODB: 'mongodb',
  TIBERO: 'tibero',
  ALTIBASE: 'altibase',
  CLICKHOUSE: 'clickhouse',
  REDSHIFT: 'redshift',
  SINGLESTORE: 'singlestore',
} as const;

const INTERMAX_PLATFORM_MENU = {
  INFRA: 'infrastructure',
  MESSAGE_QUEUE: 'message-queue',
  APP: 'application',
  BUSINESS: 'business',
  LOGS: 'logs',
  ALERT: 'alert',
};

export const TOTAL_PLATFORM_MENU = {
  ...PLATFORM_MENU,
  ...MAXGAUGE_PLATFORM_MENU,
} as const;

export type PlatformMenuType = (typeof TOTAL_PLATFORM_MENU)[keyof typeof TOTAL_PLATFORM_MENU];
export type DashboardId = number;
export type PlatformDashboardList = {
  [K in PlatformMenuType]: Record<DashboardId, PlatformDashboardValue>; // number : dashboard id
};

export type AnalysisBoardId = number;
export type PlatformAnalysisBoardList = {
  [K in PlatformMenuType]: Record<AnalysisBoardId, PlatformDashboardValue>; // number : dashboard id
};

export const usePlatformDashboardStore = defineStore('platformDashboard', () => {
  const { viewMode } = useViewModeStore();

  const platformMenu = computed<
    typeof PLATFORM_MENU | typeof MAXGAUGE_PLATFORM_MENU | typeof INTERMAX_PLATFORM_MENU
  >(() => {
    const menuMapping = {
      [VIEW_MODE.EXEMONE]: PLATFORM_MENU,
      [VIEW_MODE.MAXGAUGE]: MAXGAUGE_PLATFORM_MENU,
      [VIEW_MODE.INTERMAX]: INTERMAX_PLATFORM_MENU,
      [VIEW_MODE.CLOUDMOA]: PLATFORM_MENU,
    };

    return menuMapping[viewMode] || PLATFORM_MENU;
  });

  // Vue Query: Mutations
  const { mutateAsync: postDashboardMutate } = usePostV7MenuPathDashboard();
  const { mutateAsync: patchDashboardMutate } = usePatchV7MenuPathDashboard();
  const { mutateAsync: postAnalysisMutate } = usePostV7MenuPathAnalysis();
  const { mutateAsync: patchAnalysisMutate } = usePatchV7MenuPathAnalysis();

  // Vue Query: 플랫폼 대시보드 메뉴 데이터
  const { data: menuPathData } = useGetV7MenuPath();

  // 쿼리 키
  const menuPathQueryKey = getGetV7MenuPathQueryKey();

  // 빈 플랫폼 메뉴 객체 생성 헬퍼
  const createEmptyPlatformMenu = <T extends PlatformDashboardList | PlatformAnalysisBoardList>() =>
    Object.values(platformMenu.value).reduce((acc, key) => {
      acc[key] = {};
      return acc;
    }, {} as T);

  // 플랫폼별 대시보드 메뉴 (computed)
  const menuByPlatform = computed<PlatformDashboardList>(() => {
    const response = menuPathData.value;
    if (!response) return createEmptyPlatformMenu<PlatformDashboardList>();

    const data = response.data?.data;
    const result = createEmptyPlatformMenu<PlatformDashboardList>();

    Object.keys(result).forEach((platform) => {
      data?.forEach((item) => {
        const menuPaths = convertMenuPaths(item.menuPaths ?? []);
        if (menuPaths.includes(platform)) {
          result[platform][item.dashboardId!] = {
            name: item.dashboardName,
            favoriteId: item.favoriteId,
            iconId: item.iconId,
          } as PlatformDashboardValue;
        }
      });
    });

    return result;
  });

  // 플랫폼별 분석보드 메뉴 (computed)
  const menuByAnalysisPlatform = computed<PlatformAnalysisBoardList>(() => {
    const response = menuPathData.value;
    if (!response) return createEmptyPlatformMenu<PlatformAnalysisBoardList>();

    const data = response.data?.data;
    const result = createEmptyPlatformMenu<PlatformAnalysisBoardList>();

    Object.keys(result).forEach((platform) => {
      data?.forEach((item) => {
        const analysisMenuPaths = convertMenuPaths(item.analysisMenuPaths ?? []);
        if (analysisMenuPaths?.includes(platform)) {
          result[platform][item.dashboardId!] = {
            name: item.dashboardName,
            favoriteId: item.favoriteId,
            iconId: item.iconId,
          } as PlatformDashboardValue;
        }
      });
    });

    return result;
  });

  // 캐시 무효화 헬퍼
  const invalidateMenuPath = () => {
    queryClient.invalidateQueries({ queryKey: menuPathQueryKey });
  };

  // Optimistic update 헬퍼: 대시보드 menuPaths 수정
  const optimisticUpdateDashboard = async (
    dashboardId: number,
    dashboardName: string,
    platform: string,
    action: 'add' | 'remove',
  ) => {
    await queryClient.cancelQueries({ queryKey: menuPathQueryKey });

    const previousData = queryClient.getQueryData<GetV7MenuPathQueryResult>(menuPathQueryKey);

    queryClient.setQueryData<GetV7MenuPathQueryResult>(menuPathQueryKey, (old) => {
      if (!old?.data?.data) return old;

      const platformPath = convertPlatform(platform);
      const updatedData = old.data.data.map((item) => {
        if (item.dashboardId !== dashboardId) return item;

        const currentPaths = item.menuPaths ?? [];
        const newPaths =
          action === 'add'
            ? [...currentPaths, platformPath]
            : currentPaths.filter((p) => p !== platformPath);

        return { ...item, menuPaths: newPaths };
      });

      // add인 경우 기존 아이템이 없으면 추가
      if (action === 'add' && !updatedData.some((item) => item.dashboardId === dashboardId)) {
        updatedData.push({
          dashboardId,
          dashboardName,
          menuPaths: [platformPath],
        });
      }

      return {
        ...old,
        data: { ...old.data, data: updatedData },
      };
    });

    return previousData;
  };

  // Optimistic update 헬퍼: 분석보드 analysisMenuPaths 수정
  const optimisticUpdateAnalysis = async (
    dashboardId: number,
    dashboardName: string,
    platform: string,
    action: 'add' | 'remove',
  ) => {
    await queryClient.cancelQueries({ queryKey: menuPathQueryKey });

    const previousData = queryClient.getQueryData<GetV7MenuPathQueryResult>(menuPathQueryKey);

    queryClient.setQueryData<GetV7MenuPathQueryResult>(menuPathQueryKey, (old) => {
      if (!old?.data?.data) return old;

      const platformPath = convertPlatform(platform);
      const updatedData = old.data.data.map((item) => {
        if (item.dashboardId !== dashboardId) return item;

        const currentPaths = item.analysisMenuPaths ?? [];
        const newPaths =
          action === 'add'
            ? [...currentPaths, platformPath]
            : currentPaths.filter((p) => p !== platformPath);

        return { ...item, analysisMenuPaths: newPaths };
      });

      // add인 경우 기존 아이템이 없으면 추가
      if (action === 'add' && !updatedData.some((item) => item.dashboardId === dashboardId)) {
        updatedData.push({
          dashboardId,
          dashboardName,
          analysisMenuPaths: [platformPath],
        });
      }

      return {
        ...old,
        data: { ...old.data, data: updatedData },
      };
    });

    return previousData;
  };

  // 롤백 헬퍼
  const rollback = (previousData: GetV7MenuPathQueryResult | undefined) => {
    if (previousData) {
      queryClient.setQueryData(menuPathQueryKey, previousData);
    }
  };

  /**
   * 플랫폼에 대쉬보드 추가
   */
  const addDashboardByPlatform = async (
    platform: PlatformMenuType,
    dashboardId: number,
    dashboardName: string,
  ) => {
    // Optimistic update
    const previousData = await optimisticUpdateDashboard(
      dashboardId,
      dashboardName,
      platform,
      'add',
    );

    try {
      const request: IntegrateDashboardGnbPatchRequest = {
        dashboardId,
        menuPaths: [convertPlatform(platform)],
      };
      const {
        data: { status },
      } = await postDashboardMutate({ data: request });

      if (status !== 'success') {
        rollback(previousData);
        return false;
      }

      invalidateMenuPath();
      return true;
    } catch {
      rollback(previousData);
      return false;
    }
  };

  const removeDashboardByPlatForm = async (platform: PlatformMenuType, dashboardId: number) => {
    // Optimistic update
    const previousData = await optimisticUpdateDashboard(dashboardId, '', platform, 'remove');

    try {
      const request: IntegrateDashboardGnbPatchRequest = {
        dashboardId,
        menuPaths: [convertPlatform(platform)],
      };
      const {
        data: { status },
      } = await patchDashboardMutate({ data: request });

      if (status !== 'success') {
        rollback(previousData);
        return false;
      }

      invalidateMenuPath();
      return true;
    } catch {
      rollback(previousData);
      return false;
    }
  };

  const removeDashboardListByPlatform = (dashboardIds: number[]) => {
    // NOTE: 이 함수는 로컬에서만 대시보드 목록을 제거하는 용도였으나,
    // API 호출 없이 캐시만 무효화하여 서버 상태와 동기화
    if (dashboardIds.length > 0) {
      invalidateMenuPath();
    }
  };

  /**
   * 플랫폼에 분석보드 추가
   */
  const addAnalysisBoardByPlatform = async (
    platform: PlatformMenuType,
    dashboardId: number,
    dashboardName: string,
  ) => {
    // Optimistic update
    const previousData = await optimisticUpdateAnalysis(
      dashboardId,
      dashboardName,
      platform,
      'add',
    );

    try {
      const request: IntegrateDashboardAnalysisPatchRequest = {
        dashboardId,
        analysisMenuPaths: [convertPlatform(platform)],
      };
      const {
        data: { status },
      } = await postAnalysisMutate({ data: request });

      if (status !== 'success') {
        rollback(previousData);
        return false;
      }

      invalidateMenuPath();
      return true;
    } catch {
      rollback(previousData);
      return false;
    }
  };

  const removeAnalysisBoardByPlatForm = async (platform: PlatformMenuType, dashboardId: number) => {
    // Optimistic update
    const previousData = await optimisticUpdateAnalysis(dashboardId, '', platform, 'remove');

    try {
      const request: IntegrateDashboardAnalysisPatchRequest = {
        dashboardId,
        analysisMenuPaths: [convertPlatform(platform)],
      };
      const {
        data: { status },
      } = await patchAnalysisMutate({ data: request });

      if (status !== 'success') {
        rollback(previousData);
        return false;
      }

      invalidateMenuPath();
      return true;
    } catch {
      rollback(previousData);
      return false;
    }
  };

  return {
    platformMenu,
    menuByPlatform,
    menuByAnalysisPlatform,
    addDashboardByPlatform,
    removeDashboardByPlatForm,
    removeDashboardListByPlatform,
    addAnalysisBoardByPlatform,
    removeAnalysisBoardByPlatForm,
  };
});
